import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonComponent from "components/button/ButtonComponent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { REGEX } from "constants/validate";
import prefectures from "util/prefecture";
import useStoreList from "../hooks/useStoreList";
import { Store } from "../types/store";
import StoreFormModal from "../components/StoreFormModal";

const SearchFieldLabel = styled("span")({
  backgroundColor: "var(--primary-500)",
  color: "var(--black)",
  padding: "10px",
  borderRadius: "4px",
  marginRight: "5px",
  whiteSpace: "nowrap",
  display: "inline-block",
  width: "200px",
  textAlign: "center",
});

const StyledInput = styled("input")(({ theme }) => ({
  width: "100%",
  padding: "10px",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
  border: "1px solid var(--silver-500)",
  borderRadius: "4px",
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: "var(--primary-500)",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledBodyTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: "center",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: "var(--offwhite-300)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--white)",
  },
}));

/**
 * StoreList component renders a list of stores with search and filter options.
 * It includes a header, search fields, a search button, and a table displaying store details.
 * Users can filter stores by ID/name, prefecture, and area code, and can also choose to display only available stores.
 * Clicking on a store's detail button opens a modal with more information about the selected store.
 * @example
 * return (
 *   <StoreList />
 * )
 * The rendered StoreList component.
 */
function StoreList() {
  const navigate = useNavigate();
  const [selectedStore, setSelectedStore] = useState<Store>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrefecture, setSelectedPrefecture] = useState("");

  const { stores, total, handleUpdateStore, handleSearch, params, setParams, loading } =
    useStoreList();

  const handleSelectPrefecture = (e: any) => {
    const code = e.target.value;
    if (code) {
      setSelectedPrefecture(code);
      setParams({ ...params, pref_cd: e.target.value as string });
    }
  };

  const handleClear = () => {
    if (selectedPrefecture) {
      setSelectedPrefecture("");
      setParams({ ...params, pref_cd: "" });
    }
  };

  const matches = useMediaQuery("(min-width:1024px)"); // Media query for tablet and larger screens

  const handleDetailClick = (store: Store) => {
    setSelectedStore(store);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStore(undefined);
  };

  const updateStore = async (data: Store) => {
    await handleUpdateStore(data);
    handleCloseModal();
  };

  const getPerfName = (pref_cd: string) => {
    const prefResult = prefectures.find((prefItem) => prefItem.code === pref_cd);
    return prefResult ? prefResult?.name : pref_cd;
  };

  return (
    <>
      <Box sx={{ px: { xs: 2.5, md: 3, lg: "60px" }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", px: { xs: 2.5, md: 3, lg: "60px" } }}>
        <Box
          sx={{
            mt: { xs: 3, md: 4, lg: "46px" },
            textAlign: "left",
            width: { xs: "100%", lg: "15%" },
          }}
        >
          <Typography
            variant={matches ? "h4" : "h5"}
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 33 }, color: "var(--gray-700)" }}
          >
            マスタ管理
          </Typography>
          <Typography
            variant={matches ? "h5" : "h6"}
            margin={0}
            textAlign="left"
            sx={{ fontSize: { xs: 24, lg: 28 }, color: "var(--gray-700)" }}
          >
            店舗一覧
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            mt: { xs: 3, md: 4, lg: 9 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              columnGap: "35px",
              margin: "0 auto",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <Box
              display="flex"
              gap="10px"
              marginBottom="20px"
              margin="0 auto"
              sx={{
                flexWrap: { xs: "wrap", xl: "nowrap" },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  width: { xs: "100%", lg: "440px" },
                }}
              >
                <SearchFieldLabel
                  sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, minWidth: { xs: 220, md: 160, xl: "auto" } }}
                >
                  店舗ID/店舗名
                </SearchFieldLabel>
                <StyledInput
                  type="text"
                  id="code-name"
                  placeholder="店舗ID/店舗名"
                  value={params.store_cd_or_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setParams({ ...params, store_cd_or_name: e.target.value })
                  }
                />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  width: { xs: "100%", lg: "440px" },
                }}
              >
                <SearchFieldLabel
                  sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, minWidth: { xs: 220, md: 160, xl: "auto" } }}
                >
                  都道府県
                </SearchFieldLabel>
                <Select
                  id="prefecture"
                  sx={{
                    minWidth: "217px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "9px 14px",
                    },
                  }}
                  value={params.pref_cd}
                  onChange={(e) => handleSelectPrefecture(e)}
                  displayEmpty
                  renderValue={(value: string) => {
                    if (!selectedPrefecture) {
                      return <Typography color="var(--gray-200)">都道府県</Typography>;
                    }
                    return prefectures.find((pref) => pref.code === value)?.name || "";
                  }}
                >
                  {prefectures.map((pref) => (
                    <MenuItem key={pref.code} value={pref.code}>
                      {pref.name}
                    </MenuItem>
                  ))}
                </Select>
                {selectedPrefecture && (
                  <IconButton onClick={handleClear} size="small">
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  width: { xs: "100%", lg: "440px" },
                }}
              >
                <SearchFieldLabel
                  sx={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    minWidth: { xs: 220, md: 160, xl: "auto" },
                  }}
                >
                  管轄物流コード
                </SearchFieldLabel>
                <StyledInput
                  type="text"
                  id="area-code"
                  placeholder="管轄物流コード"
                  value={params.managed_logistics_cd}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.value.match(REGEX.INCLUDE_NUMBER_ONLY_AND_STRING_EMPTY)) {
                      setParams({ ...params, managed_logistics_cd: e.target.value });
                    }
                  }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={params.status_flag === 1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setParams({ ...params, status_flag: Number(e.target.checked) })
                    }
                    color="secondary"
                  />
                }
                // label="有効店舗のみ"
                label={
                  <Typography sx={{ fontSize: "18px", color: "var(--black)" }}>
                    有効のみ
                  </Typography>
                }
                sx={{ fontSize: "18px", whiteSpace: "pre" }}
              />
            </Box>

            <Box textAlign="center" margin="0 auto" paddingTop="30px">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "var(--gray-500)",
                  color: "var(--white)",
                  width: "190px",
                  height: "50px",
                  fontSize: { xs: 18, lg: 25 },
                  marginTop: "20px",
                  borderRadius: "9px",
                  ":hover": {
                    backgroundColor: "var(--gray-250)",
                    boxShadow: "var(--shadow)",
                  },
                }}
                onClick={handleSearch}
              >
                検索
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            width: { lg: "15%" },
            display: { xs: "none", lg: "block" },
          }}
        >
          &nbsp;
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          paddingBottom="20px"
          sx={{ width: { xs: "100%", lg: "calc(100%-30%)" } }}
        >
          <Box
            display="flex"
            justifyContent="end"
            fontSize="20px"
            sx={{ width: "100%", marginRight: { xs: "0", lg: "40px" } }}
            marginBottom="15px"
          >
            件数【{total}件】
          </Box>
          <TableContainer
            component={Table}
            sx={{
              width: "100%",
              boxShadow: "none",
            }}
          >
            <StyledTableHead>
              <TableRow>
                <StyledTableCell>店舗ID</StyledTableCell>
                <StyledTableCell>店舗名</StyledTableCell>
                <StyledTableCell>都道府県</StyledTableCell>
                <StyledTableCell>管轄物流コード</StyledTableCell>
                <StyledTableCell>詳細</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {stores.map((store, index) => (
                <StyledTableRow
                  key={`store-${store.id}`}
                  className={index % 2 === 0 ? "even" : "odd"}
                >
                  <StyledBodyTableCell>{store.store_cd}</StyledBodyTableCell>
                  <StyledBodyTableCell>
                    {store.mst_store_store_name ?? store.store_name}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell>{getPerfName(store.pref_cd)}</StyledBodyTableCell>
                  <StyledBodyTableCell>{store.managed_logistics_cd}</StyledBodyTableCell>
                  <StyledBodyTableCell>
                    <ButtonComponent
                      onClick={() => handleDetailClick(store)}
                      sx={{
                        color: "var(--black)",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        borderRadius: "4px",
                        width: { xs: "100%", xl: "172px" },
                        padding: { xs: "10px 0", lg: "10px 15px" },
                        height: "40px",
                      }}
                      label="詳細"
                    />
                  </StyledBodyTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </TableContainer>
        </Box>
      </Box>

      {selectedStore && (
        <StoreFormModal
          storeDetail={selectedStore}
          visible={isModalOpen}
          isLoading={loading}
          onClose={handleCloseModal}
          onHandleSubmit={updateStore}
        />
      )}
    </>
  );
}

export default StoreList;
